import React, { useState } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import { isWebPlatform } from '../utils/webLayout';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import type { ReportPreviewColumn, ReportPreviewRow } from '../types/report';

const CELL_MAX = 80;

type Props = {
  columns: ReportPreviewColumn[];
  rows: ReportPreviewRow[];
};

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '—';
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '—';
    return trimmed.length > CELL_MAX
      ? `${trimmed.slice(0, CELL_MAX)}…`
      : trimmed;
  }
  // Never render raw objects/arrays as JSON in the table.
  return '—';
}

function fullCellValue(value: unknown): string {
  if (value === null || value === undefined) return 'Empty';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : 'Unavailable';
  }
  if (typeof value === 'string') {
    return value.trim() || 'Empty';
  }
  return 'Value cannot be displayed';
}

export function ReportPreviewTable({ columns, rows }: Props) {
  const palette = useAppColors();
  const { isCompact, isWide } = useResponsiveLayout();
  const [expanded, setExpanded] = useState<{
    label: string;
    value: string;
  } | null>(null);

  if (!columns.length) {
    return (
      <Text style={[styles.empty, { color: palette.textMuted }]}>
        No columns were returned for this report.
      </Text>
    );
  }

  if (!rows.length) {
    return (
      <Text
        style={[styles.empty, { color: palette.textMuted }]}
        accessibilityRole="text"
      >
        No rows matched the selected filters.
      </Text>
    );
  }

  const useCards = isCompact || (columns.length > 4 && !isWide);

  if (useCards) {
    return (
      <View style={styles.cardList}>
        {rows.map((row, rowIndex) => (
          <View
            key={row.rowKey}
            style={[
              styles.card,
              {
                borderColor: palette.border,
                backgroundColor: palette.surface,
              },
            ]}
            accessibilityLabel={`Row ${rowIndex + 1}`}
          >
            {columns.map(column => {
              const raw = row.values[column.key];
              const display = formatCellValue(raw);
              return (
                <Pressable
                  key={`${row.rowKey}-${column.key}`}
                  accessibilityRole="button"
                  accessibilityLabel={`${column.label}: ${display}`}
                  onPress={() =>
                    setExpanded({
                      label: column.label,
                      value: fullCellValue(raw),
                    })
                  }
                  style={styles.cardRow}
                >
                  <Text style={[styles.cardLabel, { color: palette.textMuted }]}>
                    {column.label}
                  </Text>
                  <Text
                    style={[styles.cardValue, { color: palette.textPrimary }]}
                  >
                    {display}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        ))}
        <Text style={[styles.hint, { color: palette.textMuted }]}>
          Tap a value to view the full text when it is truncated.
        </Text>
        <ValueModal
          expanded={expanded}
          onClose={() => setExpanded(null)}
        />
      </View>
    );
  }

  return (
    <View>
      <Text
        style={[styles.hint, { color: palette.textMuted }]}
        accessibilityRole="text"
      >
        Scroll horizontally to see all columns.
      </Text>
      <ScrollView
        horizontal
        accessibilityLabel="Report preview table"
        contentContainerStyle={
          isWebPlatform() || isWide ? { minWidth: '100%' } : undefined
        }
      >
        <View>
          <View
            style={[styles.headerRow, { backgroundColor: palette.surface }]}
          >
            {columns.map(column => (
              <Text
                key={column.key}
                style={[
                  styles.headerCell,
                  {
                    color: palette.textPrimary,
                    borderColor: palette.border,
                  },
                ]}
                accessibilityRole="header"
              >
                {column.label}
              </Text>
            ))}
          </View>
          {rows.map((row, rowIndex) => (
            <View
              key={row.rowKey}
              style={[
                styles.bodyRow,
                rowIndex % 2 === 1
                  ? { backgroundColor: palette.surface }
                  : null,
              ]}
            >
              {columns.map(column => {
                const raw = row.values[column.key];
                const display = formatCellValue(raw);
                return (
                  <Pressable
                    key={`${row.rowKey}-${column.key}`}
                    accessibilityRole="button"
                    accessibilityLabel={`${column.label}: ${display}`}
                    onPress={() =>
                      setExpanded({
                        label: column.label,
                        value: fullCellValue(raw),
                      })
                    }
                    style={[styles.bodyCell, { borderColor: palette.border }]}
                  >
                    <Text
                      style={[
                        styles.cellText,
                        { color: palette.textSecondary },
                      ]}
                    >
                      {display}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          ))}
        </View>
      </ScrollView>
      <ValueModal expanded={expanded} onClose={() => setExpanded(null)} />
    </View>
  );
}

function ValueModal({
  expanded,
  onClose,
}: {
  expanded: { label: string; value: string } | null;
  onClose: () => void;
}) {
  const palette = useAppColors();
  if (!expanded) return null;
  return (
    <View
      style={[
        styles.modalCard,
        {
          backgroundColor: palette.surface,
          borderColor: palette.border,
        },
      ]}
      accessibilityViewIsModal
    >
      <Text style={[styles.modalTitle, { color: palette.textPrimary }]}>
        {expanded.label}
      </Text>
      <Text style={[styles.modalBody, { color: palette.textSecondary }]}>
        {expanded.value}
      </Text>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Close"
        onPress={onClose}
      >
        <Text style={[styles.modalClose, { color: palette.primary }]}>
          Close
        </Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  empty: {
    ...typography.body,
    marginVertical: spacing.md,
  },
  hint: {
    ...typography.caption,
    marginBottom: spacing.sm,
    marginTop: spacing.sm,
  },
  headerRow: { flexDirection: 'row' },
  headerCell: {
    ...typography.caption,
    fontWeight: '700',
    width: 140,
    padding: spacing.sm,
    borderBottomWidth: 1,
  },
  bodyRow: { flexDirection: 'row' },
  bodyCell: {
    width: 140,
    padding: spacing.sm,
    borderBottomWidth: 1,
  },
  cellText: { ...typography.caption },
  cardList: { gap: spacing.sm },
  card: {
    borderWidth: 1,
    borderRadius: 12,
    padding: spacing.md,
    gap: spacing.xs,
  },
  cardRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  cardLabel: {
    ...typography.caption,
    flex: 1,
    minWidth: 96,
  },
  cardValue: {
    ...typography.caption,
    flex: 1.4,
    minWidth: 96,
    textAlign: 'right',
  },
  modalCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: spacing.lg,
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  modalTitle: {
    ...typography.sectionTitle,
  },
  modalBody: { ...typography.body },
  modalClose: {
    ...typography.button,
    marginTop: spacing.sm,
  },
});
